import { h } from "preact";
import { useActionEmitter } from "../../../hooks/action-emitter";
import { useAnalytics } from "../../../hooks/analytics";
import { useAssetLoader } from "../../../hooks/asset-loader";
import { useExternalActionHandler } from "../../../hooks/external-action-handler";
import {
  useProgramActionHandler,
  useSingleProgramFilter,
} from "../../../hooks/program";
import { useProgramFragmentManager } from "../../../hooks/program-screen-manager";
import { useRouterActionHandler } from "../../../hooks/router";
import { IProgramFilter } from "../../../interfaces/campaign-filter";
import { IFragment } from "../../../interfaces/fragment";
import { Info } from "../../info";
import { CenterLoadingSpinner } from "../../loading-spinner";
import ActivityGameRoot1, {
  IActivityGameRoot1Layouts,
} from "../../routes/activtiy-game-root-1";
import GameChallenge, {
  IGameChallengeRoot1Layouts,
} from "../../routes/game-challenge-root-1";
import MultiStepRoot1, {
  IMultiStepRoot1Layouts,
} from "../../routes/multi-step-root-1";
import ReferralRoot1, {
  IReferralRoot1Layouts,
} from "../../routes/referral-root1";
import StreakRoot1, { IStreakRoot1Layouts } from "../../routes/streak-root-1";
import { ThemeWrapper } from "../../theme-wrapper";
import { ErrorWrapper } from "../error-wrapper";
import { LoadingWrapper } from "../loading-wrapper";

export interface IProgramWrapper {
  type_id: "PROGRAM_WRAPPER";
  filter: IProgramFilter;
  fragment: {
    rootId: string;
  };
}

export function ProgramWrapper({ filter, fragment }: IProgramWrapper) {
  const [program, programLoadStatus] = useSingleProgramFilter(filter);
  const [rootId, root, selectedFragment] = useProgramFragmentManager(
    program,
    fragment
  );
  const [onRouterAction] = useRouterActionHandler();
  const [onExternalAction] = useExternalActionHandler();
  const [programStatus, onProgramAction] = useProgramActionHandler();

  const [onAnalyticsAction] = useAnalytics({
    rootTypeId: root?.type_id,
    rootId: rootId,
    pageName: root ? root["pageName"] : null,
    program,
  });

  const [status, emit, onAction] = useActionEmitter(
    [onExternalAction, onRouterAction, onProgramAction, onAnalyticsAction],
    null
  );
  useAssetLoader(selectedFragment);

  if (
    programLoadStatus.state === "LOADING" ||
    programLoadStatus.state === "IDEL"
  ) {
    return <LoadingWrapper status={programLoadStatus} onAction={onAction} />;
  } else if (programLoadStatus.state === "LOAD_ERROR") {
    return <ErrorWrapper status={programLoadStatus} onAction={onAction} />;
  }

  if (programStatus.state === "LOADING")
    return <LoadingWrapper status={programStatus} onAction={onAction} />;

  if (!root) return;

  if (root.type_id === "MULTI_STEP_ROOT_1") {
    return (
      <ThemeWrapper theme={program.layout.theme}>
        <MultiStepRoot1
          {...root}
          screen={selectedFragment as IFragment<IMultiStepRoot1Layouts>}
          program={program}
          onAction={onAction}
        />
      </ThemeWrapper>
    );
  } else if (root.type_id === "STREAK_LAYOUT") {
    return (
      <ThemeWrapper theme={program.layout.theme}>
        <StreakRoot1
          {...root}
          screen={selectedFragment as IFragment<IStreakRoot1Layouts>}
          program={program}
          onAction={onAction}
        />
      </ThemeWrapper>
    );
  } else if (root.type_id === "ACTIVITY_GAME_ROOT_1") {
    return (
      <ThemeWrapper theme={program.layout.theme}>
        <ActivityGameRoot1
          {...root}
          screen={selectedFragment as IFragment<IActivityGameRoot1Layouts>}
          program={program}
          onAction={onAction}
        />
      </ThemeWrapper>
    );
  } else if (root.type_id === "GAME_CHALLENGE") {
    return (
      <ThemeWrapper theme={program.layout.theme}>
        <GameChallenge
          {...root}
          screen={selectedFragment as IFragment<IGameChallengeRoot1Layouts>}
          program={program}
          onAction={onAction}
        />
      </ThemeWrapper>
    );
  } else if (root.type_id === "REFERRAL_ROOT_1") {
    return (
      <ThemeWrapper theme={program.layout.theme}>
        <ReferralRoot1
          {...root}
          screen={selectedFragment as IFragment<IReferralRoot1Layouts>}
          program={program}
          onAction={onAction}
        />
      </ThemeWrapper>
    );
  }
}
